import { useEffect, useState } from "react";
import { Bell, BellOff, BellRing } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

/**
 * Web Push permission button — Dashboard header pill.
 *
 * State machine:
 *   loading      → checking browser support + server config
 *   unsupported  → browser has no PushManager / Notification API
 *   disabled     → VAPID key not configured on server → hide entirely
 *   default      → not yet asked → offer "Get notified"
 *   granted+sub  → subscribed on server → offer "Notifications on"
 *   granted-sub  → permission granted but not sent to server → auto-subscribe
 *   denied       → user blocked → show muted state
 */

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

export default function PushNotifyButton() {
  const [status, setStatus] = useState("loading"); // loading|unsupported|disabled|default|subscribed|denied
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      // 1. Feature detect
      if (
        typeof window === "undefined" ||
        !("serviceWorker" in navigator) ||
        !("PushManager" in window) ||
        !("Notification" in window)
      ) {
        setStatus("unsupported");
        return;
      }
      // 2. Server config
      let cfg;
      try {
        cfg = await api.get("/push/config");
      } catch {
        setStatus("disabled");
        return;
      }
      if (!cfg?.enabled || !cfg?.public_key) {
        setStatus("disabled");
        return;
      }
      // 3. Current permission
      if (Notification.permission === "denied") {
        setStatus("denied");
        return;
      }
      // 4. Existing subscription?  (guard: navigator.serviceWorker.ready hangs
      //    if no SW is registered — so bail out via getRegistration() first)
      try {
        const reg = await navigator.serviceWorker.getRegistration();
        if (reg) {
          const sub = await reg.pushManager.getSubscription();
          if (sub && Notification.permission === "granted") {
            setStatus("subscribed");
            return;
          }
        }
      } catch {
        // fall through — offer subscribe
      }
      setStatus("default");
    })();
  }, []);

  const subscribe = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const cfg = await api.get("/push/config");
      if (!cfg?.enabled || !cfg?.public_key) {
        toast.error("Notifications not configured.");
        setStatus("disabled");
        return;
      }
      const perm = await Notification.requestPermission();
      if (perm !== "granted") {
        setStatus(perm === "denied" ? "denied" : "default");
        if (perm === "denied") {
          toast.error("Notifications blocked. Enable in browser settings.");
        }
        return;
      }
      // Register the SW on-demand (dev builds skip auto-registration).
      let reg = await navigator.serviceWorker.getRegistration();
      if (!reg) {
        try {
          reg = await navigator.serviceWorker.register("/service-worker.js");
        } catch (e) {
          console.warn("[push] SW register failed", e);
          toast.error("Could not enable notifications (SW).");
          return;
        }
      }
      // Wait for the SW to be active (with a 10s timeout)
      if (!reg.active) {
        await Promise.race([
          navigator.serviceWorker.ready,
          new Promise((resolve) => setTimeout(resolve, 10000)),
        ]);
        reg = (await navigator.serviceWorker.getRegistration()) || reg;
      }
      const applicationServerKey = urlBase64ToUint8Array(cfg.public_key);
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey,
      });
      const json = sub.toJSON();
      await api.post("/push/subscribe", {
        endpoint: json.endpoint,
        keys: json.keys,
        tag: "d1-return",
      });
      setStatus("subscribed");
      toast.success("Notifications on. The ember will call you back.");
    } catch (e) {
      console.warn("[push] subscribe failed", e);
      toast.error("Could not enable notifications.");
    } finally {
      setBusy(false);
    }
  };

  const unsubscribe = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.getRegistration();
      const sub = reg ? await reg.pushManager.getSubscription() : null;
      if (sub) {
        const json = sub.toJSON();
        try {
          await api.post("/push/unsubscribe", {
            endpoint: json.endpoint,
            keys: json.keys,
          });
        } catch {
          // best effort — still unsub locally
        }
        await sub.unsubscribe();
      }
      setStatus("default");
      toast.success("Notifications off.");
    } catch (e) {
      console.warn("[push] unsubscribe failed", e);
    } finally {
      setBusy(false);
    }
  };

  if (status === "loading" || status === "unsupported" || status === "disabled") {
    return null;
  }
  if (status === "denied") {
    return (
      <button
        type="button"
        disabled
        title="Enable notifications in your browser settings"
        className="panel px-3 py-1.5 flex items-center gap-1.5 opacity-60 cursor-not-allowed"
        data-testid="push-notify-blocked"
      >
        <BellOff size={12} />
        <span className="text-[9px] tracking-[0.3em] ash-text">MUTED</span>
      </button>
    );
  }
  if (status === "subscribed") {
    return (
      <button
        type="button"
        onClick={unsubscribe}
        disabled={busy}
        title="Notifications are on. Click to turn off."
        className="panel px-3 py-1.5 flex items-center gap-1.5 border-[#d4af37]/50 hover:border-[#d4af37] transition-colors"
        data-testid="push-notify-on"
      >
        <BellRing size={12} className="gold-text" />
        <span className="text-[9px] tracking-[0.3em] gold-text">NOTIFY ON</span>
      </button>
    );
  }
  // default
  return (
    <button
      type="button"
      onClick={subscribe}
      disabled={busy}
      title="Get notified when your lives return + LiveOps events start"
      className="panel px-3 py-1.5 flex items-center gap-1.5 hover:border-[#d4af37] transition-colors"
      data-testid="push-notify-btn"
    >
      <Bell size={12} className="ash-text" />
      <span className="text-[9px] tracking-[0.3em] ash-text">
        {busy ? "…" : "NOTIFY ME"}
      </span>
    </button>
  );
}
